import React, { useState, useCallback, useMemo } from "react";
import crStyles from "../../../customerReservation/crStyles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import styles from "./CustomerOptOffline.module.scss";
import sharedStyles from "../OrderDetailsNewVersion.module.scss";
import FormControl from "@mui/material/FormControl";
import { withTranslation } from "react-i18next";
import CapitalizeText from "../../../../utils/CapitalizeText";
import UiDateUtils from "../../../../utils/UiDateUtils";
import CalendarDialog from "../calendarDialog/CalendarDialog";
import { OFFLINEDOCSIGN, PARTIALOFFLINE } from "../../../../common/Constants";
import { HAS_VALUE, IS_CRC_USER } from "../../../../utils/EmpUtil";
import Grid from "@mui/material/Grid";
import {
  StyledExpansionPanel,
  StyledExpansionPanelSummary,
} from "../../../../shared/ExpansionPanels/ExpansionPanel";

const OrderInfo = ({
                     actionDate,
                     userId,
                     dateAgreed,
                     recordedBy,
                     dateSigned,
                   }) => {
  return (
    actionDate && (
      <Grid container className={styles.detailsDiv}>
        <Grid item xs={1}>
          <span className={styles.subTitleText}>
            {UiDateUtils.formattedDate(actionDate)}
          </span>
        </Grid>
        {userId && (
          <Grid item xs={11}>
            <span className={styles.recordedBy}>{recordedBy}</span>
            <span className={styles.dataText}>{CapitalizeText(userId)}</span>
          </Grid>
        )}
        <Grid item xs={1} />
        {dateAgreed && (
          <Grid item xs={3}>
            <span className={styles.recordedBy}>{dateSigned}</span>
            <span className={styles.dataText}>
              {UiDateUtils.formattedDate(dateAgreed)}
            </span>
          </Grid>
        )}
      </Grid>
    )
  );
};

function CustomerOptOffline({
                              user,
                              offlineDocInfo,
                              t,
                              handleCustomerOptOffline,
                              customerReservation,
                              offlinePricingUpdate,
                              isOrderCancelled,
                              ...restProps
                            }) {
  const [expandCustomerOptOffline, setExpandCustomerOptOffline] = useState(true);

  const canEditOptOffline = useMemo(() => {
    return user.permissions.rolePermissions.hasOwnProperty("customerOptOffline:write");
  }, [user.permissions.rolePermissions]);

  const handleExpandCustomerOptOffline = useCallback(() => {
    setExpandCustomerOptOffline(prev => !prev);
  }, []);

  const saveCustomerOptOffline = useCallback((customerSignedDate) => {
    handleCustomerOptOffline(OFFLINEDOCSIGN, customerSignedDate, null);
  }, [handleCustomerOptOffline]);

  const savePricingUpdate = useCallback((customerSignedDate) => {
    handleCustomerOptOffline(PARTIALOFFLINE, customerSignedDate, null);
  }, [handleCustomerOptOffline]);

  const showPricingUpdate = useCallback((signAtStore, actionDate) => {
    return signAtStore ? actionDate : true;
  }, []);

  return (
    <StyledExpansionPanel
      defaultExpanded={true}
      onChange={handleExpandCustomerOptOffline}
    >
      <StyledExpansionPanelSummary style={crStyles.headerBarBg}>
        <div className={sharedStyles.headerBar}>
          <div
            data-testid="customerOptOfflineTitle"
            className={sharedStyles.header}
          >
            {offlineDocInfo.signAtStore
              ? t("CustomerOptOffline.customerOptAndPayTitle")
              : t("CustomerOptOffline.customerOptTitle")}
          </div>
          <div>
            {expandCustomerOptOffline ? (
              <ExpandLessIcon id="less" />
            ) : (
              <ExpandMoreIcon id="more" />
            )}
          </div>
        </div>
      </StyledExpansionPanelSummary>
      <div className={styles.infoDiv}>
        {offlineDocInfo.signAtStore && (
          <FormControl>
            <CalendarDialog
              user={user}
              offlineDocInfo={offlineDocInfo}
              customerReservation={customerReservation}
              offlinePricingUpdate={offlinePricingUpdate}
              isOrderCancelled={isOrderCancelled}
              t={t}
              {...restProps}
              id="customerOrderForm"
              saveCalendarDate={saveCustomerOptOffline}
              todayDate={customerReservation.todayDate}
              checkBoxText={t("CustomerOptOffline.customerHasSignedOrder")}
              isDisabled={
                IS_CRC_USER(user.userType) ||
                !canEditOptOffline ||
                HAS_VALUE(offlineDocInfo.dealerActionDate)
              }
              isChecked={HAS_VALUE(offlineDocInfo.dealerActionDate)}
              hasMaxDate={true}
              dialogTitle={t("CustomerOptOffline.dialogTitle")}
              dialogDescription={t("CustomerOptOffline.dialogDescription")}
            />
            <OrderInfo
              actionDate={offlineDocInfo.dealerActionDate}
              userId={offlineDocInfo.dealerUserId}
              dateAgreed={offlineDocInfo.customerSignedDate}
              recordedBy={t("CustomerOptOffline.recordedBy")}
              dateSigned={t("CustomerOptOffline.dateSigned")}
            />
          </FormControl>
        )}

        {showPricingUpdate(
          offlineDocInfo.signAtStore,
          offlineDocInfo.dealerActionDate
        ) && (
          <FormControl>
            <CalendarDialog
              user={user}
              offlineDocInfo={offlineDocInfo}
              customerReservation={customerReservation}
              offlinePricingUpdate={offlinePricingUpdate}
              isOrderCancelled={isOrderCancelled}
              t={t}
              {...restProps}
              id="pricingUpdateForm"
              saveCalendarDate={savePricingUpdate}
              showTooltip={true}
              todayDate={customerReservation.todayDate}
              checkBoxText={t("CustomerOptOffline.pricingUpdate")}
              isDisabled={
                IS_CRC_USER(user.userType) ||
                offlinePricingUpdate.partialOffline ||
                isOrderCancelled
              }
              hasMaxDate={true}
              dialogTitle={t("CustomerOptOffline.pricingDialogTitle")}
              dialogDescription={t("CustomerOptOffline.pricingDialogDescription")}
              isChecked={offlinePricingUpdate.partialOffline}
            />
            {offlinePricingUpdate.partialOffline && (
              <OrderInfo
                actionDate={offlinePricingUpdate.dealerActionDate}
                userId={offlinePricingUpdate.dealerActionUserId}
                dateAgreed={offlinePricingUpdate.dealerAcceptedDate}
                recordedBy={t("CustomerOptOffline.recordedBy")}
                dateSigned={t("CustomerOptOffline.dateSigned")}
              />
            )}
          </FormControl>
        )}
      </div>
    </StyledExpansionPanel>
  );
}

export default withTranslation("emp")(CustomerOptOffline);
